import { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Table, Badge } from "react-bootstrap";
import {
  FaIndustry,
  FaFileAlt,
  FaEnvelope,
  FaCheckCircle,
  FaExclamationTriangle
} from "react-icons/fa";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from "recharts";

import SupplierReadinessCard from "../components/Dashboard/SupplierReadinessCard";
import {
  DashboardStats,
  fetchDashboardStats
} from "../services/dashboard/api";

/* COLORS */
const COLORS = ["#16a34a", "#f59e0b", "#dc2626", "#3b82f6", "#8b5cf6"];

/* ICON MAPPER */
const ICON_COLOR = "#ffd02a";

const getIcon = (icon: string) => {
  const commonProps = {
    size: 20,
    style: { color: ICON_COLOR }
  };

  switch (icon) {
    case "industry":
      return <FaIndustry {...commonProps} />;
    case "file":
      return <FaFileAlt {...commonProps} />;
    case "message":
      return <FaEnvelope {...commonProps} />;
    case "check":
      return <FaCheckCircle {...commonProps} />;
    case "warning":
      return <FaExclamationTriangle {...commonProps} />;
    default:
      return <FaFileAlt {...commonProps} />;
  }
};

const emptyDashboard: DashboardStats = {
  kpis: [
    { label: "Suppliers", value: 0, icon: "industry" },
    { label: "Products", value: 0, icon: "file" },
    { label: "Open RFQs", value: 0, icon: "message" },
    { label: "Import Substitution", value: 0, icon: "check" }
  ],
  secondaryKpis: [
    { label: "OEM", value: 0, icon: "industry" },
    { label: "RFQ Responses", value: 0, icon: "message" },
    { label: "Matching Results", value: 0, icon: "check" },
    { label: "AI Docs Review", value: 0, icon: "warning" }
  ],
  rfqActivity: [
    { day: "J-6", rfq: 0 },
    { day: "J-5", rfq: 0 },
    { day: "J-4", rfq: 0 },
    { day: "J-3", rfq: 0 },
    { day: "J-2", rfq: 0 },
    { day: "J-1", rfq: 0 },
    { day: "Today", rfq: 0 }
  ],
  readiness: [
    { name: "OEM Ready", value: 0 },
    { name: "Upgrade Required", value: 0 },
    { name: "Blocked", value: 0 }
  ],
  industrialCapacity: [
    { name: "Non renseigné", value: 0, count: 0, capacity: 0, color: "#3b82f6" }
  ],
  aiMatching: {
    rfq: "Aucune RFQ",
    suppliers: 0,
    matchScore: 0,
    quality: "À qualifier",
    bestCandidate: "Aucun fournisseur recommandé",
    note: "Données matching à qualifier",
    warning: "Aucune alerte matching"
  },
  databaseCoverage: [],
  rfqPipeline: [],
  topWilayas: [],
  supplierSegments: [],
  documentAiStatus: [],
  certificationStatus: [],
  topProducts: [],
  activities: [],
  alerts: []
};

export default function Dashboard() {
  const [dashboard, setDashboard] = useState<DashboardStats>(emptyDashboard);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    fetchDashboardStats()
      .then((data) => {
        if (!mounted) return;

        if (data) {
          setDashboard({
            ...emptyDashboard,
            ...data,
            kpis: data.kpis?.length ? data.kpis : emptyDashboard.kpis,
            secondaryKpis: data.secondaryKpis?.length ? data.secondaryKpis : emptyDashboard.secondaryKpis,
            rfqActivity: data.rfqActivity?.length ? data.rfqActivity : emptyDashboard.rfqActivity,
            readiness: data.readiness?.length ? data.readiness : emptyDashboard.readiness,
            industrialCapacity: data.industrialCapacity?.length
              ? data.industrialCapacity
              : emptyDashboard.industrialCapacity,
            aiMatching: data.aiMatching || emptyDashboard.aiMatching,
            databaseCoverage: data.databaseCoverage || [],
            rfqPipeline: data.rfqPipeline || [],
            topWilayas: data.topWilayas || [],
            supplierSegments: data.supplierSegments || [],
            documentAiStatus: data.documentAiStatus || [],
            certificationStatus: data.certificationStatus || [],
            topProducts: data.topProducts || [],
            activities: data.activities || [],
            alerts: data.alerts || []
          });
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const {
    kpis,
    secondaryKpis,
    rfqActivity,
    readiness,
    industrialCapacity,
    aiMatching,
    databaseCoverage,
    rfqPipeline,
    topWilayas,
    supplierSegments,
    documentAiStatus,
    certificationStatus,
    topProducts,
    activities,
    alerts
  } = dashboard;

  const strongestCapacity = useMemo(() => {
    if (!industrialCapacity.length) return null;

    return industrialCapacity.reduce((best, item) => {
      return Number(item.value) > Number(best.value) ? item : best;
    }, industrialCapacity[0]);
  }, [industrialCapacity]);

  const getActivityColor = (type: string) => {
    switch (type) {
      case "RFQ":
        return "primary";
      case "Response":
        return "success";
      case "Alert":
        return "warning";
      case "Supplier":
        return "info";
      case "Product":
        return "dark";
      default:
        return "secondary";
    }
  };

  const getAlertStyle = (type: string) => {
    switch (type) {
      case "high":
        return {
          bg: "#fee2e2",
          color: "#dc2626"
        };
      case "medium":
        return {
          bg: "#fef3c7",
          color: "#f59e0b"
        };
      default:
        return {
          bg: "#e2e8f0",
          color: "#475569"
        };
    }
  };

  return (
    <div className="container-fluid py-4">

      {loading && (
        <div className="mb-3 small text-muted">
          Chargement des données dashboard...
        </div>
      )}

      {/* ================= KPI ================= */}
      <Row className="g-3 mb-4">
        {kpis.map((kpi, index) => (
          <Col md={3} key={index}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center gap-3">

                <div className="p-3 bg-light rounded-3">
                  {getIcon(kpi.icon)}
                </div>

                <div>
                  <h4 className="fw-bold mb-0">{kpi.value}</h4>
                  <small className="text-muted">{kpi.label}</small>
                </div>

              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ================= KPI SECONDAIRES ================= */}
      <Row className="g-3 mb-4">
        {secondaryKpis.map((kpi, index) => (
          <Col md={3} key={index}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="d-flex align-items-center gap-3">

                <div className="p-3 bg-light rounded-3">
                  {getIcon(kpi.icon)}
                </div>

                <div>
                  <h5 className="fw-bold mb-0">{kpi.value}</h5>
                  <small className="text-muted">{kpi.label}</small>
                </div>

              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ================= CHARTS ================= */}
      <Row className="g-3 mb-4">

        {/* RFQ Activity */}
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              <div className="d-flex justify-content-between mb-3">
                <h6 className="fw-semibold mb-0">RFQ Activity</h6>
                <small className="text-muted">Last 7 days</small>
              </div>

              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={rfqActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="rfq"
                    stroke="#dc2626"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>

            </Card.Body>
          </Card>
        </Col>

        {/* Supplier Readiness */}
        <Col md={4}>
          <SupplierReadinessCard readiness={readiness} />
        </Col>
      </Row>

      {/* ================= INDUSTRIAL + AI ================= */}
      <Row className="g-3 mb-4">

        {/* Capacity */}
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Industrial Capacity</h6>
                <small className="text-muted">By product category</small>
              </div>

              {/* DATA */}
              {industrialCapacity.map((item, index) => (

                <div key={index} className="mb-3">

                  {/* LABEL */}
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-medium">{item.name}</span>
                    <span className="fw-bold">{item.value}%</span>
                  </div>

                  {/* PROGRESS BAR */}
                  <div
                    className="progress"
                    style={{
                      height: "8px",
                      borderRadius: "10px",
                      backgroundColor: "#f1f5f9"
                    }}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${item.value}%`,
                        backgroundColor: item.color,
                        borderRadius: "10px"
                      }}
                    />
                  </div>

                  <small className="text-muted">
                    {item.count || 0} produit(s) • capacité: {item.capacity || 0}
                  </small>

                </div>

              ))}

              {/* FOOTER INSIGHT */}
              <div className="mt-3 pt-2 border-top">

                <small className="text-muted">
                  Strongest capacity:
                </small>

                <div className="fw-bold text-primary">
                  {strongestCapacity
                    ? `${strongestCapacity.name} (${strongestCapacity.value}%)`
                    : "Non renseigné"}
                </div>

              </div>

            </Card.Body>
          </Card>
        </Col>

        {/* AI Matching */}
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">AI Matching</h6>
                <small className="text-muted">Smart recommendation</small>
              </div>

              {/* MAIN KPI */}
              <Row className="text-center mb-3">

                <Col>
                  <small className="text-muted">RFQ</small>
                  <div className="fw-bold fs-5">{aiMatching.rfq}</div>
                </Col>

                <Col>
                  <small className="text-muted">Suppliers</small>
                  <div className="fw-bold fs-5">{aiMatching.suppliers}</div>
                </Col>

                <Col>
                  <small className="text-muted">Match Score</small>
                  <div className="fw-bold fs-5 text-success">
                    {aiMatching.matchScore}%
                  </div>
                </Col>

              </Row>

              {/* PROGRESS SCORE */}
              <div className="mb-3">

                <div className="d-flex justify-content-between">
                  <small className="text-muted">Matching Quality</small>
                  <small className="fw-bold text-success">{aiMatching.quality}</small>
                </div>

                <div className="progress mt-1" style={{ height: "8px" }}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${aiMatching.matchScore}%` }}
                  />
                </div>

              </div>

              {/* INSIGHT */}
              <div className="p-2 rounded-3 bg-light mb-3">

                <small className="text-muted">
                  Best candidate:
                </small>

                <div className="fw-semibold">
                  {aiMatching.bestCandidate}
                </div>

                <small className="text-muted">
                  {aiMatching.note}
                </small>

              </div>

              {/* ALERT / WARNING */}
              <div className="d-flex align-items-center gap-2 text-warning mb-2">
                ⚠️ <small>{aiMatching.warning}</small>
              </div>

              {/* ACTION */}
              <div className="d-flex justify-content-between mt-3">

                <button className="btn btn-sm btn-outline-dark rounded-3">
                  View RFQ
                </button>

                <button className="btn btn-sm btn-success rounded-3">
                  Compare Suppliers
                </button>

              </div>

            </Card.Body>
          </Card>
        </Col>

      </Row>

      {/* ================= DATABASE COVERAGE ================= */}
      <Row className="g-3 mb-4">
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Database Coverage</h6>
                <small className="text-muted">Modules alimentés par la base</small>
              </div>

              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th className="text-end">Count</th>
                    <th>Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {databaseCoverage.length ? (
                    databaseCoverage.map((item, index) => (
                      <tr key={index}>
                        <td className="fw-semibold">{item.module}</td>
                        <td className="text-end fw-bold">{item.count}</td>
                        <td className="text-muted small">{item.detail}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-muted small">
                        Aucune donnée disponible
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">RFQ Pipeline</h6>
                <small className="text-muted">Status</small>
              </div>

              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={rfqPipeline}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {rfqPipeline.map((entry, index) => (
                      <Cell key={index} fill={entry.color || COLORS[index] || "#64748b"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <div className="d-flex flex-column gap-1 mt-2">
                {rfqPipeline.length ? (
                  rfqPipeline.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between small">
                      <span className="text-muted">{item.name}</span>
                      <span className="fw-bold">{item.value}</span>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">Aucune RFQ</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ================= DISTRIBUTION ================= */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Top Wilayas</h6>
                <small className="text-muted">Suppliers</small>
              </div>

              <div className="d-flex flex-column gap-2">
                {topWilayas.length ? (
                  topWilayas.map((item, index) => (
                    <div key={index}>
                      <div className="d-flex justify-content-between mb-1">
                        <small className="fw-medium">{item.name}</small>
                        <small className="fw-bold">{item.value}</small>
                      </div>
                      <div className="progress" style={{ height: "6px" }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${Math.min(100, item.value * 10)}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">Aucune donnée wilaya</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Supplier Segments</h6>
                <small className="text-muted">Industrial sectors</small>
              </div>

              <div className="d-flex flex-column gap-2">
                {supplierSegments.length ? (
                  supplierSegments.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                      <span className="small fw-medium">{item.name}</span>
                      <Badge bg="dark">{item.value}</Badge>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">Aucun segment</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Document AI</h6>
                <small className="text-muted">Analysis status</small>
              </div>

              <div className="d-flex flex-column gap-2">
                {documentAiStatus.length ? (
                  documentAiStatus.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                      <span className="small fw-medium">{item.name}</span>
                      <Badge bg={item.name === "FAILED" ? "danger" : item.name === "ANALYZED" ? "success" : "warning"}>
                        {item.value}
                      </Badge>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">Aucune analyse IA</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ================= PRODUCTS + CERTIFICATIONS ================= */}
      <Row className="g-3 mb-4">
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Latest Products</h6>
                <small className="text-muted">Marketplace / supplier products</small>
              </div>

              <Table responsive hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Fournisseur</th>
                    <th>Catégorie</th>
                    <th className="text-end">Capacité</th>
                    <th className="text-end">Photos</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.length ? (
                    topProducts.map((product) => (
                      <tr key={product.idProduct}>
                        <td className="fw-semibold">{product.name}</td>
                        <td className="small text-muted">{product.supplier}</td>
                        <td><Badge bg="light" text="dark">{product.category}</Badge></td>
                        <td className="text-end small">{product.annualCapacity} {product.unit}</td>
                        <td className="text-end fw-bold">{product.imagesCount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-muted small">
                        Aucun produit
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Certifications</h6>
                <small className="text-muted">Conformity status</small>
              </div>

              <div className="d-flex flex-column gap-2">
                {certificationStatus.length ? (
                  certificationStatus.map((item, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center p-2 rounded-3" style={{ backgroundColor: "#f8fafc" }}>
                      <span className="small fw-medium">{item.name}</span>
                      <Badge bg="secondary">{item.value}</Badge>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">Aucune certification</small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ================= TABLE + ALERTS ================= */}
      <Row className="g-3">

        {/* Activity */}
        <Col md={8}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">Recent Activity</h6>
                <small className="text-muted">Live platform events</small>
              </div>

              {/* LIST */}
              <div className="d-flex flex-column gap-3">

                {activities.length ? (
                  activities.map((a, i) => {
                    const color = getActivityColor(a.type);

                    return (
                      <div
                        key={i}
                        className="d-flex align-items-start justify-content-between p-2 rounded-3"
                        style={{ backgroundColor: "#f8fafc" }}
                      >

                        {/* LEFT */}
                        <div className="d-flex align-items-start gap-3">

                          {/* DOT */}
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              marginTop: "6px",
                              backgroundColor: "var(--bs-" + color + ")"
                            }}
                          />

                          {/* CONTENT */}
                          <div>

                            <div className="fw-semibold">
                              {a.name}
                            </div>

                            <div className="small text-muted">
                              {a.type}
                            </div>

                          </div>

                        </div>

                        {/* RIGHT */}
                        <div className="text-end">

                          <div className="small text-muted">
                            {a.date}
                          </div>

                          <Badge bg={color} className="mt-1">
                            {a.type}
                          </Badge>

                        </div>

                      </div>
                    );
                  })
                ) : (
                  <div
                    className="p-2 rounded-3 text-muted small"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    Aucune activité récente
                  </div>
                )}

              </div>

              {/* FOOTER */}
              <div className="mt-3 text-end">
                <button className="btn btn-sm btn-outline-dark rounded-3">
                  View All Activity
                </button>
              </div>

            </Card.Body>
          </Card>
        </Col>

        {/* Alerts */}
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-semibold mb-0">
                  ⚠ Alerts
                </h6>
                <small className="text-muted">System warnings</small>
              </div>

              {/* LIST */}
              <div className="d-flex flex-column gap-3">

                {alerts.length ? (
                  alerts.map((a, i) => {
                    const style = getAlertStyle(a.type);

                    return (
                      <div
                        key={i}
                        className="p-2 rounded-3"
                        style={{
                          backgroundColor: style.bg
                        }}
                      >

                        <div className="d-flex justify-content-between align-items-start">

                          {/* TEXT */}
                          <div style={{ color: style.color }}>
                            <div className="fw-semibold small">
                              {a.message}
                            </div>

                            <small className="text-muted">
                              {a.date}
                            </small>
                          </div>

                          {/* PRIORITY */}
                          <span
                            className="badge"
                            style={{
                              backgroundColor: style.color,
                              color: "#fff"
                            }}
                          >
                            {a.type}
                          </span>

                        </div>

                      </div>
                    );
                  })
                ) : (
                  <div
                    className="p-2 rounded-3 text-muted small"
                    style={{
                      backgroundColor: "#e2e8f0"
                    }}
                  >
                    Aucune alerte
                  </div>
                )}

              </div>

              {/* FOOTER */}
              <div className="mt-3 text-end">
                <button className="btn btn-sm btn-outline-dark rounded-3">
                  View All Alerts
                </button>
              </div>

            </Card.Body>
          </Card>
        </Col>

      </Row>

    </div>
  );
}
